<template>
  <BCard>
    <div class="flex flex-row justify-between items-center">
      <div class="text-black text-2xl font-medium">
        Verifikasi Data Diri
      </div>
      <div
        v-if="dataLatestHistory !== null"
        class="flex items-center gap-4"
      >
        <div v-if="dataLatestHistory.status !== 'not requested'">
          {{ DAY_MONTH_YEAR(dataLatestHistory.last_updated) }} | {{ TIME(dataLatestHistory.last_updated) }}
        </div>
        <BButton
          v-if="['not requested', 'rejected'].includes(dataLatestHistory.status)"
          variant="primary"
          size="sm"
          style="border-radius: 25px !important"
          @click="$bvModal.show('modal-rules')"
        >
          Petunjuk Verifikasi
          <BIcon icon="question-circle" />
        </BButton>
      </div>
    </div>
    <ValidationObserver v-slot="{ invalid }">
      <BForm v-if="Object.entries(dataLatestHistory).length !== 0">
        <BRow>
          <BCol lg="4">
            <BCard body-class="!pb-0">
              <div class="font-medium text-black text-lg">
                Verifikasi Wajah Liveness
              </div>
              <div>Aktifkan kamera dan ikuti petunjuk di layar</div>
              <FaceDetection
                ref="faceDetection"
                @snapshot-captured="handleSnapshotCaptured"
              />

              <div
                v-if="!isCameraActive"
                class="font-medium text-black text-lg flex gap-1"
              >
                <div>Verifikasi Wajah</div>
                <div
                  v-if="!isEmpty(dataLatestHistory.liveness_picture.status) || verificationComplete"
                  class="flex"
                >
                  <img
                    src="https://storage.googleapis.com/komerce/assets/icons/Checklist-fill.svg"
                    alt="Komerce"
                  >
                  <div
                    v-if="!isEmpty(dataLatestHistory.liveness_picture.status)"
                    :class="getClassStatus(dataLatestHistory.liveness_picture.status)"
                  >
                    {{ `(${getLabelStatus(dataLatestHistory.liveness_picture.status)})` }}
                  </div>
                </div>
              </div>
              <BButton
                v-if="canVerify"
                size="sm"
                block
                variant="primary"
                @click="$bvModal.show('modal-rules-face-detection')"
              >
                Verifikasi
              </BButton>
              <BFormTextarea
                v-if="dataLatestHistory.liveness_picture.status === 'rejected'"
                v-model="dataLatestHistory.liveness_picture.notes"
                class="mt-1"
                disabled
                rows="4"
              />
            </BCard>
          </BCol>
        </BRow>
        <BRow>
          <BCol
            v-for="file in formFiles"
            :key="file.id"
            lg="6"
          >
            <BCard body-class="flex flex-col gap-3">
              <div>
                <div class="text-black text-xl font-medium">
                  {{ file.title }}
                  <span
                    v-if="dataLatestHistory.status !== 'not requested'"
                    :class="getClassStatus(dataLatestHistory[file.id].status)"
                  >
                    ({{ getLabelStatus(dataLatestHistory[file.id].status) }})
                  </span>
                </div>
                <div class="text-[#828282] text-base">
                  {{ file.desc }}
                </div>
              </div>
              <img
                :src="handlePreviewImage(file.key, file.id)"
                alt="Komerce"
                class="rounded-xl"
              >
              <div
                v-if="['rejected', 'draft'].includes(dataLatestHistory[file.id].status) || dataLatestHistory.status === 'not requested'"
              >
                <div class="text-sm text-center mb-1">
                  Max. 1Mb
                </div>
                <ValidationProvider
                  #default="{ errors }"
                  :rules="{ required: file.rules() ? true : false }"
                >
                  <BFormFile
                    v-model="file.selected"
                    :state="errors.length > 0 ? false : null"
                    browse-text="Pilih file"
                    :placeholder="file.fileName"
                    :disabled="isDisabledFormFile"
                    @change="file.onChange($event)"
                  />
                  <BFormTextarea
                    v-if="dataLatestHistory.status === 'rejected'"
                    v-model="file.notes"
                    class="mt-1"
                    disabled
                    rows="4"
                  />
                  <div
                    v-if="allowFileSize[file.key]"
                    class="flex items-center gap-2 mt-1"
                  >
                    <img
                      src="https://storage.googleapis.com/komerce/assets/icons/danger-red.svg"
                      alt="Komerce"
                      width="15"
                    >
                    <small class="text-primary">Ukuran file terlalu besar</small>
                  </div>
                </ValidationProvider>
              </div>
            </BCard>
          </BCol>
        </BRow>

        <div class="flex flex-row items-start">
          <img
            src="https://storage.googleapis.com/komerce/assets/komerce-icon/Hitam/user.svg"
            alt="Komerce"
          >
          <div class="ml-[10px]">
            <span class="text-black font-bold text-lg">
              Informasi Data Diri
            </span>
            <div class="text-black">
              <span class="text-danger">*</span>
              <span class="text-[#828282]">Pastikan dokumen yang di-upload sesuai dengan data diri kamu</span>
            </div>
          </div>
        </div>

        <BRow class="mt-3 mb-1">
          <BCol
            lg="12"
            class="flex items-center"
          >
            <BCol
              lg="4"
              class="text-lg"
            >
              Pengguna Produk
            </BCol>
            <BCol
              lg="6"
              class="flex items-center"
            >
              <div
                v-for="(product, key) in dataLatestHistory.product_partner"
                :key="key"
              >
                <img
                  :src="product.url_image"
                  alt="Komerce"
                  width="30"
                >
              </div>
            </BCol>
          </BCol>
        </BRow>

        <BRow
          v-if="submitProgress === 100 || ['approved', 'rejected', 'draft'].includes(dataLatestHistory.status)"
          class="justify-center items-center gap-[10px]"
        >
          <BCol
            lg="12"
            class="flex items-center"
          >
            <BCol
              lg="4"
              class="text-lg"
            >
              <span>NIK</span>
            </BCol>
            <BCol lg="6">
              <ValidationProvider
                ref="nikInput"
                #default="{ errors }"
                name="NIK"
                rules="required|min:16"
              >
                <BFormInput
                  v-model="nik"
                  :state="errors.length > 0 ? false : null"
                  :disabled="isDisabledIdentityNumber ? true : false"
                  maxlength="16"
                  placeholder="Masukkan NIK sesuai dengan foto"
                  @keypress="isNumber($event)"
                  @blur="checkNIK()"
                />
                <BRow class="justify-between">
                  <small class="text-danger ml-1">{{ errors[0] }}</small>
                  <small class="mr-1">{{ nik.length }}/16</small>
                </BRow>
              </ValidationProvider>
            </BCol>
          </BCol>

          <BCol
            v-for="(field, index) in formFields"
            :key="index"
            lg="12"
            class="flex items-center"
          >
            <BCol
              lg="4"
              class="text-lg"
            >
              <span>{{ field.label }}</span>
            </BCol>
            <BCol lg="6">
              <ValidationProvider
                #default="{ errors }"
                :name="field.label"
                :rules="field.rules"
              >
                <BButton
                  v-if="field.type === 'date'"
                  size="sm"
                  block
                  variant="outline-secondary"
                  style="border: 1px solid #d8d6de !important"
                >
                  <BRow class="justify-arround items-center">
                    <Datepicker
                      v-model="birthDate"
                      :language="id"
                      wrapper-class="border-solid border-slate-200 rounded w-full"
                      input-class="w-[90%] text-[#6e6b7b] ml-1"
                      calendar-class="border-none text-[#6e6b7b]"
                    />
                    <b-img
                      src="https://storage.googleapis.com/komerce/assets/icons/date-picker-icon.svg"
                      class="w-5"
                    />
                  </BRow>
                </BButton>

                <BFormInput
                  v-else
                  v-model="dataLatestHistory.info_data_partner[field.key]"
                  :state="errors.length > 0 ? false : null"
                  :placeholder="field.placeholder"
                  :disabled="field.disabled"
                  @keypress="field.keypress && field.keypress($event)"
                  @blur="field.blur && field.blur()"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </ValidationProvider>
            </BCol>
          </BCol>
        </BRow>
        <div
          v-if="shouldShowCheckbox && submitProgress === 100 || ['approved', 'rejected', 'draft'].includes(dataLatestHistory.status)"
          class="mt-2"
        >
          <b-form-checkbox
            v-model="agreeTerms"
            class="custom-control-primary mb-1"
          >
            Saya menyetujui kebijakan verifikasi identitas KTP. <a
              class="underline text-primary"
              :href="term"
              target="_blank"
            >Lihat detail</a>
          </b-form-checkbox>
        </div>
        <BRow class="justify-end m-2">
          <div class="flex gap-2 mt-1">
            <BButton
              variant="outline-primary"
              @click="validateBack()"
            >
              Kembali
            </BButton>
            <BButton
              v-if="['not requested', 'rejected', 'draft'].includes(dataLatestHistory.status)"
              :variant="invalid || birthDate === 'Invalid date' || isValidationFile || canVerify || (isCheckboxRequired && !agreeTerms) ? 'secondary' : 'primary'"
              :disabled="invalid || birthDate === 'Invalid date' || isValidationFile || canVerify || (isCheckboxRequired && !agreeTerms)"
              @click="$bvModal.show(idModal)"
            >
              Ajukan Verifikasi
            </BButton>
          </div>
        </BRow>
      </BForm>
    </ValidationObserver>

    <ModalVerificationKyc
      :id-modal="idModal"
      :title="'Pengajuan Verifikasi KTP'"
      :desc="'Pastikan bahwa file KTP dan Selfie yang anda ajukan telah benar'"
      :source="'verif'"
      :loading="loadingSubmit"
      :submit="onSubmit"
    />
    <RulesVerification />
    <ModalConfirm
      id="modal-validation-out"
      title="Kamu Yakin Keluar?"
      :message="''"
      label-confirm-button="Keluar"
      label-cancel-button="Batal"
      @on-click-cancel-button="$bvModal.hide('modal-validation-out')"
      @on-click-confirm-button="back()"
    />

    <BModal
      id="modal-progress-kyc"
      ref="modal-progress-kyc"
      no-stacking
      centered
      hide-footer
      hide-header
      hide-header-close
      no-close-on-backdrop
      no-close-on-esc
      size="md"
    >
      <div class="modal-loader-container mt-2">
        <div class="loader-wrapper">
          <div class="loader" />
          <div class="progress-text">
            {{ submitProgress }}%
          </div>
        </div>
        <div class="mt-1 text-lg text-center text-black">
          Sedang memproses verifikasi...
        </div>
      </div>
    </BModal>

    <ModalRulesFaceDetection @start-verification="startVerification" />
    <ModalPaidVerification />
  </BCard>
</template>

<script>
import { mapState } from 'vuex'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required, min } from '@validations'
import { newAxiosIns } from '@/libs/axios'
import { DASH_DATE, DAY_MONTH_YEAR, TIME } from '@/libs/formatDate'
import {
  isNumber, isAlphabet, isNotNumber,
} from '@/libs/helpers'
import ModalConfirm from '@/views/components/modal/ModalConfirm.vue'
import Datepicker from 'vuejs-datepicker'
import moment from 'moment'
import { id } from 'vuejs-datepicker/dist/locale'
import DOMPurify from 'dompurify'
import isEmpty from 'lodash/isEmpty'
import RulesVerification from './RulesVerification.vue'
import ModalVerificationKyc from '../../pages/komcard/components/ModalVerificationKyc.vue'
import { getLabelStatus, getClassStatus } from '../../pages/komcard/config'
import FaceDetection from './FaceDetection.vue'
import ModalRulesFaceDetection from './ModalRulesFaceDetection.vue'
import ModalPaidVerification from './ModalPaidVerification.vue'

export default {
  components: {
    ValidationObserver,
    ValidationProvider,
    RulesVerification,
    ModalVerificationKyc,
    ModalConfirm,
    Datepicker,
    FaceDetection,
    ModalRulesFaceDetection,
    ModalPaidVerification,
  },
  data() {
    return {
      getLabelStatus,
      getClassStatus,
      DAY_MONTH_YEAR,
      TIME,
      isNumber,
      isAlphabet,
      isNotNumber,
      isEmpty,
      id,
      preview: {
        selfie: null,
        idCard: null,
      },
      selectedFile: {
        selfie: null,
        idCard: null,
      },
      name: {
        selfie: null,
        idCard: null,
      },
      allowFileSize: {
        selfie: false,
        idCard: false,
      },
      allowFileExt: {
        selfie: false,
        idCard: false,
      },
      size: {
        selfie: 0,
        idCard: 0,
      },
      term: '',
      agreeTerms: false,
      required,
      min,
      loadingSubmit: false,
      loadingLatestHistory: false,
      loadingUpload: false,
      dataLatestHistory: {},
      idModal: 'modal-confirm-kyc',
      isBack: false,
      nextRoute: '',
      nik: '',
      submitProgress: 0,
      progressInterval: null,
      hasUploaded: false,
      birthDate: '',

      isCameraActive: false,
      verificationFile: null,
      verificationComplete: false,
      previewUrl: null,
    }
  },
  computed: {
    ...mapState('dashboard', ['profile']),
    ...mapState('route', ['previousRoute']),
    shouldShowCheckbox() {
      return this.profile.is_komship !== this.profile.is_komcard
    },
    isCheckboxRequired() {
      return this.shouldShowCheckbox
    },
    isDisabledIdentityNumber() {
      return ['approved', 'requested'].includes(this.dataLatestHistory.status)
    },
    isValidationFile() {
      const conditions = [
        this.allowFileSize.selfie,
        this.allowFileSize.idCard,
      ]
      return conditions.some(condition => condition)
    },
    canVerify() {
      const invalidStatus = ['rejected']
      return !this.verificationComplete
        && !this.isCameraActive
        && (!this.dataLatestHistory?.liveness_picture?.status
          || invalidStatus.includes(this.dataLatestHistory.liveness_picture.status))
    },
    isDisabledFormFile() {
      const invalidStatus = ['rejected']
      return !this.verificationComplete
        && (!this.dataLatestHistory?.liveness_picture?.status
          || invalidStatus.includes(this.dataLatestHistory.liveness_picture.status))
    },
    formFiles() {
      const data = [
        {
          id: 'ktp_face_picture',
          key: 'selfie',
          title: 'Foto wajah beserta KTP kamu',
          desc: 'Unggah foto diri beserta KTP kamu',
          fileName: this.name.selfie || 'Belum ada file yang dipilih',
          value: this.preview.selfie,
          selected: this.selectedFile.selfie,
          notes: this.dataLatestHistory?.ktp_face_picture?.notes,
          onChange: e => this.uploadValidation('selfie', e),
          rules: () => {
            const confitions = [
              this.dataLatestHistory.ktp_face_picture.status === 'rejected',
              this.dataLatestHistory.status === 'not requested',
            ]
            return confitions.some(condition => condition)
          },
        },
        {
          id: 'ktp_picture',
          key: 'idCard',
          title: 'Foto KTP kamu',
          desc: '  Unggah foto KTP kamu',
          fileName: this.name.idCard || 'Belum ada file yang dipilih',
          value: this.preview.idCard,
          selected: this.selectedFile.idCard,
          notes: this.dataLatestHistory?.ktp_picture?.notes,
          onChange: e => this.uploadValidation('idCard', e),
          rules: () => {
            const conditions = [
              this.dataLatestHistory.ktp_picture.status === 'rejected',
              this.dataLatestHistory.status === 'not requested',
            ]
            return conditions.some(condition => condition)
          },
        },
      ]

      return data
    },
    formFields() {
      const data = [
        {
          key: 'name',
          label: 'Nama',
          type: 'text',
          placeholder: 'Masukkan nama',
          disabled: false,
          rules: 'required',
          keypress: e => this.isNotNumber(e),
        },
        {
          key: 'birth_place',
          label: 'Tempat Lahir',
          type: 'text',
          name: 'Tempat Lahir',
          rules: 'required',
          placeholder: 'Masukkan Tempat Lahir',
          disabled: false,
          keypress: e => this.isNotNumber(e),
        },
        {
          key: 'birth_date',
          label: 'Tanggal Lahir',
          type: 'date',
          placeholder: 'Masukkan tanggal lahir',
          rules: 'required',
          disabled: false,
        },
        {
          key: 'province',
          label: 'Provinsi',
          type: 'text',
          placeholder: 'Masukkan provinsi',
          disabled: false,
          rules: 'required',
          keypress: e => this.isNotNumber(e),
        },
        {
          key: 'city',
          label: 'Kabupaten/Kota',
          type: 'text',
          placeholder: 'Masukkan kabupaten/kota',
          disabled: false,
          rules: 'required',
          keypress: e => this.isNotNumber(e),
        },
        {
          key: 'district',
          label: 'Kecamatan',
          type: 'text',
          placeholder: 'Masukkan kecamatan',
          disabled: false,
          rules: 'required',
          keypress: e => this.isNotNumber(e),
        },
        {
          key: 'village',
          label: 'Kelurahan/Desa',
          type: 'text',
          placeholder: 'Masukkan kelurahan/desa',
          disabled: false,
          rules: 'required',
          keypress: e => this.isNotNumber(e),
        },
        {
          key: 'address',
          label: 'Alamat',
          type: 'text',
          placeholder: 'Masukkan alamat',
          disabled: false,
          rules: 'required',
        },
        {
          key: 'rt_rw',
          label: 'RT/RW',
          type: 'text',
          placeholder: 'Masukkan RT/RW',
          disabled: false,
          rules: 'required',
        },
        {
          key: 'pos_code',
          label: 'Kode Pos',
          type: 'text',
          placeholder: 'Masukkan kode pos',
          disabled: false,
          rules: 'required',
          keypress: e => this.isNumber(e),
        },
      ]

      return data
    },
  },
  watch: {
    'selectedFile.selfie': function (newVal) {
      if (newVal) this.checkAndUpload()
    },
    'selectedFile.idCard': function (newVal) {
      if (newVal) this.checkAndUpload()
    },
    verificationComplete(newVal) {
      if (newVal) this.checkAndUpload()
    },
  },
  beforeDestroy() {
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl)
    }

    if (this.progressInterval) {
      clearInterval(this.progressInterval)
    }
  },
  mounted() {
    if (this.profile.verification_ktp_status === 0) {
      this.$bvModal.show('modal-paid-verification')
    }

    this.latestHistory()
    window.onbeforeunload = event => {
      event.preventDefault()
      return true
    }
  },
  // beforeRouteLeave(to, _, next) {
  //   this.nextRoute = to.fullPath
  //   if (this.preview.selfie !== null || this.preview.idCard !== null || this.nik !== null || this.dataLatestHistory.name !== null) {
  //     this.$bvModal.show('modal-validation-out')
  //     next(this.isBack)
  //   } else {
  //     next(true)
  //   }
  // },
  created() {
    this.setTerm()
    this.debouncedUpload = this.debounce(this.onUpload, 500)
  },
  methods: {
    startVerification() {
      this.$bvModal.hide('modal-rules-face-detection')
      this.isCameraActive = true
      this.$refs.faceDetection.enableCamera()
    },
    handleSnapshotCaptured(file) {
      this.verificationFile = file
      this.verificationComplete = true
      this.isCameraActive = false
      this.previewUrl = file
    },
    validateBack() {
      this.$bvModal.show('modal-validation-out')
    },
    back() {
      this.isBack = true
      this.$bvModal.hide('modal-validation-out')
      if (this.nextRoute === '') {
        this.$router.go(-1)
      } else {
        this.$router.push(this.nextRoute)
      }
    },
    async latestHistory() {
      this.loadingLatestHistory = true
      try {
        const url = '/auth/api/v1/partner/request-verification-ktp/latest-history'
        const { data: { data } } = await newAxiosIns.get(url)
        this.loadingLatestHistory = false
        this.dataLatestHistory = data
        this.nik = data.info_data_partner.nik
        this.birthDate = moment(this.dataLatestHistory.info_data_partner.birth_date, 'DD-MM-YYYY').format('"YYYY-MM-DD"')
      } catch (error) {
        this.loadingLatestHistory = false
      }
    },
    checkUploadEligibility() {
      // Jangan lanjut jika sedang upload atau sudah pernah upload
      if (this.loadingUpload || this.hasUploaded) return false

      // Tidak lanjut jika ada error file
      const hasFileError = this.allowFileSize.selfie || this.allowFileSize.idCard || this.allowFileExt.selfie || this.allowFileExt.idCard

      if (hasFileError) return false

      const selfieSelected = !!this.selectedFile.selfie
      const idCardSelected = !!this.selectedFile.idCard

      const selfieRejected = ['rejected', 'draft'].includes(this.dataLatestHistory?.ktp_face_picture?.status)
      const idCardRejected = ['rejected', 'draft'].includes(this.dataLatestHistory?.ktp_picture?.status)
      const livenessRejected = ['rejected'].includes(this.dataLatestHistory?.liveness_picture?.status)

      // Jika liveness rejected
      if (livenessRejected) {
        if (!this.verificationComplete) return false
        if (selfieRejected && !selfieSelected) return false
        if (idCardRejected && !idCardSelected) return false
        return true
      }

      // Jika kedua file dipilih dan tidak ada yang rejected, jalankan upload
      if (selfieSelected && idCardSelected && !selfieRejected && !idCardRejected) {
        return true
      }

      // Jika kedua file rejected → wajib pilih ulang kedua file → upload
      if (selfieRejected && idCardRejected) {
        return selfieSelected && idCardSelected
      }

      // Jika hanya selfie yang rejected dan hanya selfie yang dipilih
      if (selfieRejected && !idCardRejected && selfieSelected) {
        return true
      }

      // Jika hanya idCard yang rejected dan hanya idCard yang dipilih
      if (idCardRejected && !selfieRejected && idCardSelected) {
        return true
      }

      // Kondisi lain untuk file yang sudah ada sebelumnya (existing logic)
      const filePreviouslyExists = {
        selfie: !!this.dataLatestHistory?.ktp_face_picture?.url,
        idCard: !!this.dataLatestHistory?.ktp_picture?.url,
      }

      // File sebelumnya ada dan diganti
      const conditionReplaced = (
        (filePreviouslyExists.selfie && selfieSelected)
        || (filePreviouslyExists.idCard && idCardSelected)
      )

      if (conditionReplaced) {
        return true
      }

      // Default: tidak upload
      return false
    },
    debounce(func, delay = 300) {
      let timeout
      return function debounceFn(...args) {
        clearTimeout(timeout)
        timeout = setTimeout(() => {
          func.apply(this, args)
        }, delay)
      }
    },
    checkAndUpload() {
      if (this.checkUploadEligibility()) {
        this.debouncedUpload()
      }
    },
    async onUpload() {
      this.loadingUpload = true
      this.startProgressLoader()

      try {
        const formData = new FormData()
        if (this.selectedFile.selfie) {
          formData.append('image_ktp_with_face', this.selectedFile.selfie)
        }
        if (this.selectedFile.idCard) {
          formData.append('image_ktp', this.selectedFile.idCard)
        }
        if (this.previewUrl) {
          formData.append('image_liveness', this.previewUrl)
        }

        const url = '/auth/api/v2/partner/ktp-verification/upload'
        await newAxiosIns.post(url, formData)
        await this.latestHistory()
        this.clearProgressLoader()
      } catch (err) {
        this.clearProgressLoader()

        const { response: { data: { meta } } } = err

        if (meta.message === 'nik already used by other user') {
          this.$toast_error({ message: 'NIK sudah digunakan, periksa kembali NIK kamu!' })
        } else {
          this.$toast_error({ message: 'Terjadi kesalahan. Periksa koneksi atau coba lagi nanti' })
        }
      }
    },
    async onSubmit() {
      this.loadingSubmit = true
      const url = '/auth/api/v1/partner/ktp-verification'
      const nik = !isEmpty(this.nik) ? this.nik : this.dataLatestHistory.info_data_partner.nik
      const payload = {
        nik: DOMPurify.sanitize(nik),
        name: DOMPurify.sanitize(this.dataLatestHistory.info_data_partner.name),
        email: DOMPurify.sanitize(this.dataLatestHistory.info_data_partner.email),
        phone: DOMPurify.sanitize(this.dataLatestHistory.info_data_partner.phone),
        birth_place: DOMPurify.sanitize(this.dataLatestHistory.info_data_partner.birth_place),
        birth_date: DASH_DATE(this.birthDate),
        province: DOMPurify.sanitize(this.dataLatestHistory.info_data_partner.province),
        city: DOMPurify.sanitize(this.dataLatestHistory.info_data_partner.city),
        district: DOMPurify.sanitize(this.dataLatestHistory.info_data_partner.district),
        village: DOMPurify.sanitize(this.dataLatestHistory.info_data_partner.village),
        address: DOMPurify.sanitize(this.dataLatestHistory.info_data_partner.address),
        rt_rw: DOMPurify.sanitize(this.dataLatestHistory.info_data_partner.rt_rw),
        pos_code: DOMPurify.sanitize(this.dataLatestHistory.info_data_partner.pos_code),
      }

      await newAxiosIns
        .post(url, payload)
        .then(() => {
          this.loadingSubmit = false
          this.isBack = true
          this.$bvModal.hide(this.idModal)
          this.$bvModal.hide('modal-validation-out')
          this.$toast_success({
            message: 'Berhasil mengajukan Verifikasi KTP. Silahkan tunggu konfirmasi lebih lanjut selama 1x24 jam',
          })
          this.$store.commit('auth/SET_KTP_VERIFICATION_SUCCESS', true)
          this.resetFiles()
          if (this.previousRoute === '/whitelisted-account') {
            this.$router.push('/whitelisted-account')
          } else {
            this.$router.push('/setting-kompship/profile')
          }
        })
        .catch(() => {
          this.loadingSubmit = false
          this.$toast_error({ message: 'Ada kesalahan saat mengajukan verifikasi KTP' })
        })
    },
    startProgressLoader() {
      this.$bvModal.show('modal-progress-kyc')
      this.submitProgress = 0
      this.progressInterval = setInterval(() => {
        if (this.submitProgress < 100) {
          this.submitProgress = Math.min(this.submitProgress + 1, 100)

          if (this.submitProgress >= 100) {
            this.clearProgressLoader()
          }
        }
      }, 300)
    },
    resetFiles() {
      this.selectedFile = { selfie: null, idCard: null }
      this.preview = { selfie: null, idCard: null }
      this.name = { selfie: null, idCard: null }
      this.allowFileSize = { selfie: false, idCard: false }
      this.allowFileExt = { selfie: false, idCard: false }
    },
    uploadValidation(type, event) {
      const { target } = event
      const file = target.files[0]

      if (!file) return

      const fileSize = file.size
      const fileType = file.type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png']
      const isValidType = allowedTypes.includes(fileType)

      this.hasUploaded = false
      this.allowFileSize[type] = false
      this.allowFileExt[type] = false

      if (fileSize > 1024 * 1024) { // 1MB
        this.allowFileSize[type] = true
        this.$toast_error({ message: 'Ukuran file terlalu besar (maks 1MB).' })
        return
      }

      if (!isValidType) {
        this.allowFileExt[type] = true
        this.$toast_error({ message: 'File yang kamu masukan harus berupa format image (JPG/PNG).' })
        return
      }

      this.selectedFile[type] = file
      this.name[type] = file.name
      const reader = new FileReader()

      reader.onload = e => {
        this.preview[type] = e.target.result
        // Cek kondisi hanya setelah preview selesai dimuat
        if (this.preview.selfie || this.preview.idCard) {
          // Tunggu hingga salah satu sudah selesai terbaca (satu per satu)
          this.checkAndUpload()
        }
      }

      reader.readAsDataURL(file)
    },
    clearProgressLoader() {
      if (this.progressInterval) {
        clearInterval(this.progressInterval)
        this.progressInterval = null
      }
      this.loadingUpload = false
      this.hasUploaded = true
      this.$bvModal.hide('modal-progress-kyc')
    },
    handlePreviewImage(key, source) {
      if (this.selectedFile[key]) {
        return this.preview[key]
      }
      return this.dataLatestHistory?.[source]?.url || 'https://storage.googleapis.com/komerce/assets/illustration/placeholder-selfie.svg'
    },
    checkNIK() {
      if (this.nik.length === 16 && this.dataLatestHistory.info_data_partner.nik !== this.nik) {
        const url = '/auth/api/v1/check-nik'
        this.$http_new
          .post(`${url}?nik=${this.nik}&email=${this.dataLatestHistory.info_data_partner.email}`)
          .then(res => {
            this.$refs.nikInput.applyResult({
              errors: [],
              valid: true,
              failedRules: {},
            })
          })
          .catch(err => {
            if (err.response.data.meta.message === 'nik already inserted') {
              this.$refs.nikInput.applyResult({
                errors: ['Nomor KTP sudah digunakan'],
                valid: false,
                failedRules: {},
              })
            }
          })
      }
    },
    setTerm() {
      if (this.profile.is_komads) {
        this.term = 'https://komads.id/terms/'
      } else if (this.profile.is_komship && !this.profile.is_komcard) {
        this.term = 'https://komship.id/terms/'
      } else {
        this.term = 'https://komcards.id/syarat-dan-ketentuan/'
      }
    },
  },
}
</script>
<style scoped lang="scss">
@import './verification.scss';
</style>
